<script lang="ts" setup>
import type { WelfarePageConfig } from '../configs/types'
import WelfareGuideAddScreen from './modules/WelfareGuideAddScreen.vue'
import WelfareGuideOpen from './modules/WelfareGuideOpen.vue'
import { theme as brandTheme } from '@/config/brands'

const props = defineProps<{
  config: WelfarePageConfig
  homeBgUrl: string
  btnBgColor: string
  signed: boolean
  progress: any[]
  modalSigned: boolean
  progressInfo: any
  tomorrowInfo: any
  checkInDayNum: number
  showTabbar: boolean
  tasks: any[]
  showGuidePopupAddHome: boolean
  showGuideOpen: boolean
  finishPopup: boolean
  coins: number
  completeTaskDescription: string
  userCoinBalance: number
  getBtnStatus: (item: any) => string
  onCheckIn: () => void
  handleTask: (item: any) => void
  onCheckInOk: () => void
}>()

const emit = defineEmits<{
  (e: 'update:modalSigned', value: boolean): void
  (e: 'update:showGuidePopupAddHome', value: boolean): void
  (e: 'update:showGuideOpen', value: boolean): void
  (e: 'update:finishPopup', value: boolean): void
}>()

const topMaskBackground = `linear-gradient(180deg, ${brandTheme.bgGlobal || '#121214'} 0%, transparent 100%)`
</script>

<template>
  <div class="min-h-100dvh pb-4 bg-n-bg-global">
    <!-- Cover -->
    <div class="cover w-full relative bg-n-bg-global" :style="{ backgroundImage: homeBgUrl }">
      <!-- 深色渐变遮罩，确保背景图顶部不会是白色 -->
      <div
        class="absolute top-0 left-0 right-0 h-120px z-0 pointer-events-none"
        :style="{ background: topMaskBackground }"
      />
      <div class="relative z-2 flex justify-between items-center px-5 pt-3.5 pb-3">
        <div>
          <h1 class="text-12px text-n-text-2">
            {{ config.texts.myWallet }}
          </h1>
        </div>
        <div class="flex items-center">
          <img class="w-24px h-24px" :src="config.resources.welfareGold">
          <strong class="ml-5px text-16px text-n-text-1">{{ userCoinBalance }}</strong>
          <span class="ml-5px text-12px text-n-text-2">{{ config.texts.coins }}</span>
        </div>
      </div>
      <div class="relative z-2 mx-10px px-10px py-16px bg-n-bg-card b-rd-2 rr-welfare-module">
        <h3 class="rr-section-title text-18px font-bold text-n-text-1">
          {{ config.texts.dailyCheckIn }}
        </h3>
        <p class="text-12px text-n-text-3">
          {{ config.texts.youHaveCheckedIn }} {{ checkInDayNum }} {{ checkInDayNum === 1 ? config.texts.dayStraight : $t('daysStraight') }}
        </p>
        <div class="mt-15px px-4px">
          <!-- 第一行 4 天 -->
          <div class="grid grid-cols-4 space-x-8px">
            <div
              v-for="(item, index) in progress.slice(0, 4)"
              :key="item.num"
              class="text-center"
              :class="{ 'opacity-70': item.isSign }"
            >
              <div class="flex flex-col items-center pt-10px pb-6px bg-n-bg-tag b-rd-5px">
                <img v-if="index !== progress.length - 1" class="w-24px h-24px" :src="config.resources.welfareGold">
                <img v-else class="w-28px h-25px mb--1px" :src="config.resources.welfareBox">
                <span class="mt-3px text-10px text-n-text-1">+{{ item.prizeNum }}</span>
              </div>
              <div class="flex justify-center">
                <i-assets-icon-right v-if="item.isSign" class="mt-10px text-n-primary" />
                <span v-else class="text-10px text-n-text-3 mt-8px">{{ config.texts.day(item.num) }}</span>
              </div>
            </div>
          </div>
          <!-- 第二行 3 天 -->
          <div class="grid grid-cols-4 space-x-8px mt-8px">
            <div
              v-for="(item, index) in progress.slice(4)"
              :key="item.num"
              class="text-center"
              :class="{ 'opacity-70': item.isSign }"
            >
              <div class="flex flex-col items-center pt-10px pb-6px bg-n-bg-tag b-rd-5px">
                <img v-if="index + 4 !== progress.length - 1" class="w-24px h-24px" :src="config.resources.welfareGold">
                <img v-else class="w-28px h-25px mb--1px" :src="config.resources.welfareBox">
                <span class="mt-3px text-10px text-n-text-1">+{{ item.prizeNum }}</span>
              </div>
              <div class="flex justify-center">
                <i-assets-icon-right v-if="item.isSign" class="mt-10px text-n-primary" />
                <span v-else class="text-10px text-n-text-3 mt-8px">{{ config.texts.day(item.num) }}</span>
              </div>
            </div>
          </div>
        </div>
        <div class="flex justify-center">
          <a
            v-if="progress.length"
            class="rr-checkin-btn block mt-4 w-200px h-9 lh-9 text-15px text-center btn-solid rounded-full"
            :class="{ 'opacity-70': signed }"
            @click="onCheckIn"
          >
            <span v-if="!signed">{{ config.texts.checkIn }}</span>
            <span v-else-if="checkInDayNum > 0 && checkInDayNum === progress.length">{{ config.texts.tomorrowToGetMore }}</span>
            <span v-else>{{ config.texts.tomorrowCheckIn }} {{ tomorrowInfo?.prizeNum }}</span>
          </a>
        </div>
      </div>

      <div v-if="tasks && tasks.length" class="relative z-2 mt-10px mx-10px px-10px pt-16px pb-0 bg-n-bg-card b-rd-2 rr-welfare-module">
        <h3 class="rr-section-title text-18px font-bold text-n-text-1">
          {{ config.texts.tasks }}
        </h3>

        <div
          v-for="(item, index) in tasks"
          :key="index"
          class="flex justify-between items-center py-16px px-12px border-b-solid border-b-1px border-n-border-primary last:border-b-0"
        >
          <div>
            <div class="text-n-text-1 font-medium">
              {{ item.taskTitle }}
            </div>
            <div class="text-n-primary text-12px flex items-center mt-4px">
              <img class="w-14px h-14px" :src="config.resources.welfareGold">
              <span class="ml-1" :style="{ color: config.theme.coinsColor }">{{ `+${item.coins}` }}</span>
            </div>
          </div>

          <div
            v-if="item.buttonState !== 1"
            class="rr-task-btn w-60px text-center py-4px b-rd-15px text-12px"
            :style="{ background: config.theme.checkinBtnGradient, color: '#FFFFFF' }"
            @click="handleTask(item)"
          >
            {{ getBtnStatus(item) }}
          </div>
          <div v-else class="w-60px flex items-center justify-center">
            <i-assets-icon-right class="color-n-primary" />
          </div>
        </div>
      </div>
    </div>

    <WelfareGuideAddScreen :value="showGuidePopupAddHome" @input="emit('update:showGuidePopupAddHome', $event)" />
    <WelfareGuideOpen :value="showGuideOpen" @input="emit('update:showGuideOpen', $event)" />

    <BasicModal :value="modalSigned" @input="emit('update:modalSigned', $event)">
      <div class="p-5">
        <div class="text-17px lh-2em font-bold text-center text-n-text-1">
          <p>{{ config.texts.checkInGet }} <span class="text-n-primary">{{ progressInfo?.prizeNum }}</span> {{ config.texts.coins }}</p>
        </div>
        <a
          class="rr-modal-ok-btn block mt-4 w-full h-9 lh-9 text-15px text-center btn-solid rounded-full"
          @click="onCheckInOk"
        >
          {{ config.texts.ok }}
        </a>
      </div>
    </BasicModal>

    <BasicModal :value="finishPopup" @input="emit('update:finishPopup', $event)">
      <div class="p-5">
        <div class="text-17px lh-2em font-bold text-center text-n-text-1">
          <p>{{ completeTaskDescription }} {{ config.texts.get }} <span class="text-n-primary">{{ coins }}</span> {{ config.texts.coins }}</p>
        </div>
        <a
          class="rr-modal-ok-btn block mt-4 w-full h-9 lh-9 text-15px text-center btn-solid rounded-full"
          @click="emit('update:finishPopup', false)"
        >
          {{ config.texts.ok }}
        </a>
      </div>
    </BasicModal>
  </div>
</template>

<style scoped>
.rr-section-title {
  font-size: 18px;
  font-weight: 700;
  line-height: 24px;
}

.cover {
  background-repeat: no-repeat;
  background-position: top left;
  background-size: 100% auto;
}

.rr-checkin-btn {
  color: #FFFFFF !important;
}

.rr-modal-ok-btn {
  color: #FFFFFF !important;
}
</style>
